import React from "react";
import styled from "styled-components";
import { useMutation, queryCache } from "react-query";
import { Modal, Button, Input, DatePicker, Typography } from "antd";
import { useForm, Controller } from "react-hook-form";
import moment from "moment";
import useSearchUser from "hooks/api/user/useSearchUser";
import { NotificationStateContext } from "context/UI/Notification/NotificationState";
import { apiAxios } from "services/HttpServices";
import { removeEmptyFields } from "lib/utils";

const { Text } = Typography;

const updateDateline = async ({ jobId, datelineId, dateline }) => {
  try {
    const res = await apiAxios.put(
      `jobs/${jobId}/dateline/${datelineId}`,
      dateline
    );
    return res.data.data;
  } catch (e) {
    throw e.response.data.message;
  }
};

export const EditTaskModal = (props) => {
  const {
    modalVisible,
    selectedDateline,
    handleCloseModal,
    jobId,
    datelineId,
  } = props;
  const { control, handleSubmit, reset, setValue, formState } = useForm();
  const { isDirty } = formState;
  const [keyword, setKeyword] = React.useState("");
  const [error, setError] = React.useState("");
  const { data, status: searchUserStatus } = useSearchUser(keyword);
  const {
    state: notificationState,
    setLoading,
    finishLoading,
  } = React.useContext(NotificationStateContext);

  const titleRef = React.useRef();

  const [mutateUpdate] = useMutation(updateDateline, {
    onSuccess: (datelineData, variables) => {
      handleCloseModal();
      queryCache.setQueryData(
        ["job-detail", variables.jobId.toString()],
        function (oldData) {
          const datelineIndex = oldData.dateline.findIndex(
            (dateline) => dateline.id === variables.datelineId
          );
          oldData.dateline[datelineIndex] = datelineData;
          return oldData;
        }
      );
    },
    onError: (e) => {
      setError(e);
    },
  });

  React.useEffect(() => {
    if (titleRef.current) {
      titleRef.current.focus();
    }
    if (selectedDateline) {
      reset({
        title: selectedDateline.title,
        due_at:
          selectedDateline.due_at && selectedDateline.due_at !== null
            ? moment.utc(selectedDateline?.due_at) || ""
            : "",
      });
    } else {
      reset({ title: "", due_at: undefined });
    }
  }, [selectedDateline]);

  const handleSubmitData = async (data, option) => {
    setError("");
    const dataToSubmit = {
      title: data.title,
      due_at: data.due_at ? data.due_at.format() : "",
    };

    if (data.asignees && data.asignees.length > 0) {
      dataToSubmit["asignees"] = data.asignees.map((asignee) => ({
        id: parseInt(asignee.value),
      }));
    }

    removeEmptyFields(dataToSubmit);
    setLoading("updateDateline");
    try {
      await mutateUpdate({
        jobId,
        datelineId: selectedDateline.id,
        dateline: dataToSubmit,
      });
      finishLoading("updateDateline");
    } catch (error) {
      finishLoading("updateDateline");
      console.log(error);
    }

    return dataToSubmit;
  };

  return (
    <Modal
      visible={modalVisible}
      title={`Update ${selectedDateline.title}`}
      onOk={handleSubmit(handleSubmitData)}
      onCancel={() => {
        handleCloseModal();
        setError("");
        setKeyword("");
      }}
      footer={[
        <Button
          key="back"
          onClick={() => {
            handleCloseModal();
            setError("");
            setKeyword("");
          }}
        >
          Return
        </Button>,
        <Button
          key="submit"
          type="primary"
          disabled={!isDirty}
          onClick={handleSubmit(handleSubmitData)}
          loading={notificationState.loading.updateTask}
        >
          Update
        </Button>,
      ]}
    >
      {error && <ErrorContainer>{error}</ErrorContainer>}

      <form onSubmit={handleSubmit(handleSubmitData)}>
        <StyledInput>
          <Text className="title">Title</Text>
          <Controller
            as={
              <Input
                ref={(input) => {
                  titleRef.current = input;
                }}
              />
            }
            name="title"
            placeholder="Task title"
            control={control}
            defaultValue={selectedDateline ? selectedDateline.title : ""}
          />
        </StyledInput>

        <StyledInput>
          <Text className="title">Due date</Text>
          <Controller
            as={DatePicker}
            name="due_at"
            control={control}
            defaultValue={
              selectedDateline
                ? selectedDateline.due_at !== null
                  ? moment.utc(selectedDateline.due_at)
                  : ""
                : ""
            }
            style={{ width: "100%" }}
          />
        </StyledInput>

        <Button htmlType="submit" hidden>
          Submit
        </Button>
      </form>
    </Modal>
  );
};

const StyledInput = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: ${(props) => props.theme.spacing[2]};

  .title {
    font-size: 0.8rem;
    color: ${(props) => props.theme.colors.grey[600]};
  }
`;

const ErrorContainer = styled.div`
  padding: ${(props) => props.theme.spacing[2]};
  background-color: ${(props) => props.theme.colors.red[100]};
  border: 1px solid ${(props) => props.theme.colors[700]};
  font-weight: 600;
  color: ${(props) => props.theme.colors.red[500]};
  margin-bottom: ${(props) => props.theme.spacing[2]};
`;

export default EditTaskModal;
